import { assertRelationLoaded } from "../../helpers/orm";
import {
    ConnectionProviderType,
    FinancialConnectionBase,
    FinancialConnectionWithAccounts,
} from "../../types/financialConnection";
import {
    StripeConnectionKind,
    StripeConnectionScope,
} from "../../types/stripeConnection";
import { UserDto } from "../user/user.dto";
import { IntegrationAccountDto } from "./integration-account.dto";

function isStripeConnectionScope(
    value: string,
): value is StripeConnectionScope {
    return (
        value === StripeConnectionScope.READ_WRITE ||
        value === StripeConnectionScope.READ_ONLY
    );
}

function isStripeConnectionKind(value: string): value is StripeConnectionKind {
    return (
        value === StripeConnectionKind.OAUTH ||
        value === StripeConnectionKind.EXPRESS
    );
}

/**
 * Input type for FinancialConnectionDto constructor.
 * Accepts Base type from entity classes - relations validated at runtime.
 */
export type FinancialConnectionDtoInput = FinancialConnectionBase;

export class FinancialConnectionDto {
    static deserialize(
        dto: FinancialConnectionDto,
    ): FinancialConnectionWithAccounts {
        return {
            ...dto,
            createdAt: new Date(dto.createdAt),
            user: dto.user ? UserDto.deserialize(dto.user) : null,
            accounts: dto.accounts
                ? dto.accounts.map(IntegrationAccountDto.deserialize)
                : [],
        };
    }

    id: number;
    connectionExternalId: string;
    connectionProvider: ConnectionProviderType;
    createdAt: string;
    user: UserDto | null;
    userId: number | null;
    accounts: IntegrationAccountDto[];
    error: string | null;
    scope?: StripeConnectionScope;
    // Stripe-only readiness fields. Present on Stripe connections; the invoice
    // picker + accounts/integrations Incomplete helper key off `kind` +
    // `chargesEnabled`, never off `scope`.
    kind?: StripeConnectionKind;
    detailsSubmitted?: boolean;
    chargesEnabled?: boolean;
    isAbandoned?: boolean;

    constructor(connection: FinancialConnectionDtoInput) {
        assertRelationLoaded(connection, "user");

        this.id = connection.id;
        this.connectionExternalId = connection.connectionExternalId;
        this.createdAt = connection.createdAt.toISOString();
        this.user = connection.user ? new UserDto(connection.user) : null;
        this.userId = connection.userId;
        this.connectionProvider = connection.connectionProvider;
        this.accounts = connection.accounts
            ? connection.accounts.map((fa) => new IntegrationAccountDto(fa))
            : [];
        this.error = connection.error;
        if (
            "scope" in connection &&
            typeof connection.scope === "string" &&
            isStripeConnectionScope(connection.scope)
        ) {
            this.scope = connection.scope;
        }
        if (
            "kind" in connection &&
            typeof connection.kind === "string" &&
            isStripeConnectionKind(connection.kind)
        ) {
            this.kind = connection.kind;
        }
        if (
            "detailsSubmitted" in connection &&
            typeof connection.detailsSubmitted === "boolean"
        ) {
            this.detailsSubmitted = connection.detailsSubmitted;
        }
        if (
            "chargesEnabled" in connection &&
            typeof connection.chargesEnabled === "boolean"
        ) {
            this.chargesEnabled = connection.chargesEnabled;
        }
        if ("abandonedAt" in connection) {
            this.isAbandoned = connection.abandonedAt != null;
        }
    }
}
